import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { planLimitVars, usePlanLimits } from '@/hooks/usePlanLimits'

// ── Header ────────────────────────────────────────────────────────────────

function PublicHeader() {
  const { t, i18n } = useTranslation()
  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2">
            {t('legal.switchLang')}
          </button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">{t('legal.login')}</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/signup">{t('legal.startFree')}</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}

// ── TOC items ─────────────────────────────────────────────────────────────

const TOC_JA = [
  { id: 'getting-started', label: 'はじめに' },
  { id: 'domain',          label: 'ドメイン登録' },
  { id: 'project',         label: 'プロジェクト' },
  { id: 'endpoint',        label: 'エンドポイント' },
  { id: 'scenario-static',     label: 'シナリオ: Static' },
  { id: 'scenario-sequence',   label: 'シナリオ: Sequence' },
  { id: 'scenario-conditional',label: 'シナリオ: Conditional' },
  { id: 'scenario-stateful',   label: 'シナリオ: Stateful' },
  { id: 'sharing',         label: '共有 URL' },
  { id: 'api-client',      label: 'API クライアント' },
  { id: 'plans',           label: 'プランと制限' },
]

const TOC_EN = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'domain',          label: 'Domain' },
  { id: 'project',         label: 'Projects' },
  { id: 'endpoint',        label: 'Endpoints' },
  { id: 'scenario-static',     label: 'Scenario: Static' },
  { id: 'scenario-sequence',   label: 'Scenario: Sequence' },
  { id: 'scenario-conditional',label: 'Scenario: Conditional' },
  { id: 'scenario-stateful',   label: 'Scenario: Stateful' },
  { id: 'sharing',         label: 'Sharing URL' },
  { id: 'api-client',      label: 'API Client' },
  { id: 'plans',           label: 'Plans & Limits' },
]

// ── Section ───────────────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="space-y-4 scroll-mt-20">
      <h2 className="text-xl font-bold tracking-tight border-b pb-2">{title}</h2>
      {children}
    </section>
  )
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </div>
  )
}

function Code({ children }: { children: string }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">{children}</code>
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="rounded-md bg-muted px-4 py-3 text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all">
      {children}
    </pre>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border-l-4 border-primary bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
      {children}
    </div>
  )
}

// ── Content: Japanese ────────────────────────────────────────────────────

function ContentJa() {
  const limits = planLimitVars(usePlanLimits())
  return (
    <div className="space-y-12">

      <Section id="getting-started" title="はじめに">
        <p className="text-sm text-muted-foreground leading-relaxed">
          ScenarioMock は「本物のように振る舞う Mock API」をブラウザから作成・共有できるサービスです。
          POSTで作ったリソースをGETで取得できる「ステートフル Mock」や、
          3回目だけ500を返す「シナリオエンジン」が特徴です。
        </p>
        <SubSection title="1. アカウント作成">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li><Link to="/signup" className="text-primary underline underline-offset-4">新規登録ページ</Link> でメールアドレスとパスワードを入力して登録</li>
            <li>利用規約とプライバシーポリシーに同意してアカウントを作成</li>
            <li>ダッシュボードにリダイレクトされます</li>
          </ol>
        </SubSection>
        <SubSection title="2. 基本的な流れ">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>アカウント設定でドメインを登録（Mock URL のプレフィックスになります）</li>
            <li>プロジェクトを作成（Mock URL のスラグが決まります）</li>
            <li>エンドポイントを追加（例: <Code>GET /users</Code>）</li>
            <li>シナリオを設定（返すレスポンスを定義）</li>
            <li>Mock URL にリクエストを送信して動作確認</li>
            <li>共有 URL をチームに渡す</li>
          </ol>
        </SubSection>
      </Section>

      <Separator />

      <Section id="domain" title="ドメイン登録">
        <p className="text-sm text-muted-foreground leading-relaxed">
          ドメインは Mock URL のプレフィックスとなる識別子です。<strong>アカウント作成後、最初にドメインを登録する必要があります。</strong>
          登録するとあなた専用の URL が有効になり、他のユーザーと同じスラグのプロジェクトも作成できるようになります。
        </p>
        <SubSection title="ドメインとは">
          <p className="text-sm text-muted-foreground">
            ドメインは Mock URL のプレフィックスとして使われます。登録後の URL 形式は次のとおりです:
          </p>
          <CodeBlock>{`// ドメイン登録後
https://あなたのドメイン--スラグ.scenariomock.com

// 例: ドメイン "yoshi"、スラグ "my-api" の場合
https://yoshi--my-api.scenariomock.com`}</CodeBlock>
          <Note>
            ドメインは全ユーザー横断で一意です。他のユーザーがすでに使用しているドメインは登録できません。
            スラグはドメイン登録後はあなたのアカウント内でのみ一意であればよくなります。
          </Note>
        </SubSection>
        <SubSection title="登録方法">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>サイドバーの「アカウント設定」をクリック</li>
            <li>「ドメイン」欄に希望のドメイン名を入力（3〜20文字、小文字英数字とハイフンのみ）</li>
            <li>「保存」をクリック</li>
          </ol>
        </SubSection>
        <SubSection title="ドメインの変更">
          <p className="text-sm text-muted-foreground">
            ドメインは後から変更できます。ただし、<strong>変更するとすべての Mock URL が変わります</strong>。
            既存のクライアントが旧 URL を使っている場合はアクセスできなくなるため、ご注意ください。
          </p>
        </SubSection>
      </Section>

      <Separator />

      <Section id="project" title="プロジェクト">
        <p className="text-sm text-muted-foreground leading-relaxed">
          プロジェクトは Mock API サーバーの単位です。1つのプロジェクトが1つのサブドメインに対応します。
        </p>
        <SubSection title="プロジェクトの作成">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>ダッシュボードの「新規プロジェクト」をクリック</li>
            <li><strong>スラグ</strong>: Mock URL の識別子（小文字英数字・ハイフン、3〜32文字）</li>
            <li><strong>名前</strong>: 管理用の表示名</li>
            <li><strong>公開設定</strong>: Public にすると共有 URL が使えます</li>
          </ol>
          <Note>
            スラグ例: <Code>my-api</Code>、ドメイン <Code>yoshi</Code> → Mock URL: <Code>https://yoshi--my-api.scenariomock.com</Code>
          </Note>
        </SubSection>
        <SubSection title="公開 / 非公開">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>Private</strong>: 自分だけが管理画面から操作できます</li>
            <li><strong>Public</strong>: 共有 URL (<Code>/shared/スラグ</Code>) で誰でもエンドポイント一覧を見てリクエストを送れます</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="endpoint" title="エンドポイント">
        <p className="text-sm text-muted-foreground leading-relaxed">
          エンドポイントは Mock API のルートです。HTTPメソッドとパスの組み合わせで定義します。
        </p>
        <SubSection title="エンドポイントの追加">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>プロジェクト画面で「エンドポイントを追加」をクリック</li>
            <li><strong>メソッド</strong>: GET / POST / PUT / PATCH / DELETE / HEAD</li>
            <li><strong>パス</strong>: <Code>/users</Code> や <Code>/users/:id</Code> など（必ず <Code>/</Code> で始める）</li>
            <li><strong>優先度</strong>: 同じパスに複数のエンドポイントがある場合に使います（大きいほど優先）</li>
          </ol>
        </SubSection>
        <SubSection title="パスパラメータ">
          <p className="text-sm text-muted-foreground">
            <Code>:id</Code> のような動的セグメントが使えます。
            <Code>/users/:id</Code> は <Code>/users/123</Code> にマッチします。
          </p>
        </SubSection>
        <SubSection title="Free プランの制限">
          <Note>Free プランはプロジェクトあたり最大 <strong>{limits.endpoints}件</strong> のエンドポイントを作成できます。Pro プランは無制限です。</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-static" title="シナリオ: Static">
        <p className="text-sm text-muted-foreground leading-relaxed">
          最もシンプルなシナリオです。常に同じレスポンスを返します。
        </p>
        <SubSection title="設定項目">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>ステータスコード</strong>: 200 / 201 / 400 / 404 / 500 など</li>
            <li><strong>レスポンスボディ</strong>: JSON / テキスト / XML から選択</li>
            <li><strong>レスポンスヘッダー</strong>: 任意のヘッダーを追加可能</li>
            <li><strong>遅延 (ms)</strong>: レスポンスを遅らせてネットワーク遅延を再現</li>
          </ul>
        </SubSection>
        <SubSection title="使用例">
          <CodeBlock>{`// GET /users → 200 OK
{
  "users": [
    { "id": 1, "name": "Alice" },
    { "id": 2, "name": "Bob" }
  ]
}`}</CodeBlock>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-sequence" title="シナリオ: Sequence">
        <p className="text-sm text-muted-foreground leading-relaxed">
          リクエストのたびに異なるレスポンスを順番に返します。「最初の2回は200、3回目は500」といった挙動が作れます。
        </p>
        <SubSection title="設定項目">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>ステップ</strong>: レスポンスを順番に定義（何個でも追加可）</li>
            <li><strong>ループ</strong>: ON にすると最後のステップの後に最初に戻ります</li>
            <li><strong>スコープ</strong>: カウントを全体で共有するか IP アドレス別にするかを選択</li>
          </ul>
        </SubSection>
        <SubSection title="使用例">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>ステップ 1: 200 OK（正常レスポンス）</li>
            <li>ステップ 2: 200 OK（正常レスポンス）</li>
            <li>ステップ 3: 500 Internal Server Error</li>
          </ul>
          <Note>カウンターはシナリオ詳細画面からリセットできます。</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-conditional" title="シナリオ: Conditional">
        <p className="text-sm text-muted-foreground leading-relaxed">
          リクエストの条件（ヘッダー・クエリ・ボディ・メソッド）によって異なるレスポンスを返します。
        </p>
        <SubSection title="条件の種類">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>header</strong>: リクエストヘッダーの値で判定（例: Authorization ヘッダーなし → 401）</li>
            <li><strong>query</strong>: クエリパラメータで判定</li>
            <li><strong>body</strong>: リクエストボディの JSON フィールドで判定</li>
            <li><strong>method</strong>: HTTP メソッドで判定</li>
          </ul>
        </SubSection>
        <SubSection title="演算子">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><Code>equals</Code> / <Code>not_equals</Code></li>
            <li><Code>contains</Code> / <Code>not_contains</Code></li>
            <li><Code>exists</Code> / <Code>not_exists</Code>（フィールドの存在確認）</li>
            <li><Code>regex</Code>（正規表現マッチ）</li>
          </ul>
        </SubSection>
        <SubSection title="使用例">
          <p className="text-sm text-muted-foreground">Authorization ヘッダーが存在しない場合に 401 を返す:</p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>条件: <Code>header.authorization</Code> <Code>not_exists</Code></li>
            <li>レスポンス: 401 Unauthorized</li>
            <li>デフォルト（条件に合わない場合）: 200 OK</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-stateful" title="シナリオ: Stateful Collection">
        <p className="text-sm text-muted-foreground leading-relaxed">
          POST でリソースを作成し、GET で取得、DELETE で削除できる「本物に近い CRUD API」を再現します。
          データは Redis に保存されます。
        </p>
        <SubSection title="アクション">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>create</strong>: POST でリソースを作成（ID を自動生成）</li>
            <li><strong>list</strong>: GET で全件取得</li>
            <li><strong>get</strong>: GET でID指定の1件取得</li>
            <li><strong>update</strong>: PUT / PATCH でリソースを更新</li>
            <li><strong>delete</strong>: DELETE でリソースを削除</li>
          </ul>
        </SubSection>
        <SubSection title="使用例">
          <CodeBlock>{`# リソース作成
POST https://yoshi--my-api.scenariomock.com/users
{"name": "Alice", "email": "alice@example.com"}
→ {"id": "uuid-xxx", "name": "Alice", "email": "alice@example.com"}

# 一覧取得
GET https://yoshi--my-api.scenariomock.com/users
→ [{"id": "uuid-xxx", "name": "Alice", ...}]

# ID 指定取得
GET https://yoshi--my-api.scenariomock.com/users/uuid-xxx
→ {"id": "uuid-xxx", "name": "Alice", ...}

# 削除
DELETE https://yoshi--my-api.scenariomock.com/users/uuid-xxx
→ 204 No Content`}</CodeBlock>
          <Note>コレクションのデータはシナリオ詳細画面の「コレクション」パネルからリアルタイムで確認・リセットできます。</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="sharing" title="共有 URL">
        <p className="text-sm text-muted-foreground leading-relaxed">
          プロジェクトを Public に設定すると、共有 URL でエンドポイント一覧を共有できます。
          受け取った側はアカウント不要でブラウザからリクエストを送れます。
        </p>
        <SubSection title="共有 URL の形式">
          <CodeBlock>{`https://scenariomock.com/shared/your-slug
// または
https://your-slug.scenariomock.com`}</CodeBlock>
        </SubSection>
        <SubSection title="共有ページでできること">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>エンドポイント一覧の閲覧</li>
            <li>API クライアントからリクエスト送信</li>
            <li>リクエストログのリアルタイム確認</li>
          </ul>
          <Note>オーナーがログイン中は「管理画面」ボタンが表示され、直接管理画面に移動できます。</Note>
        </SubSection>
        <SubSection title="共有ギャラリーに掲載したとき">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Public のプロジェクトは、ダッシュボードの「ギャラリーに掲載する」で共有ギャラリーに載せられます(表示名の設定が必要です)。
            ギャラリーに載せた Mock には誰でもデータを書き込めるため、掲載中は次のデータが6時間ごとに自動でリセットされます。
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Stateful Collection に保存されたデータ(ID の連番も1に戻ります)</li>
            <li>Sequence の回数</li>
          </ul>
          <Note>
            掲載した直後には消えません。最初のリクエストから6時間たったあと、次のリクエストが来たときにリセットされます。
            掲載をやめるとリセットも止まり、もう一度掲載するとそこから6時間を数え直します。
          </Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="api-client" title="API クライアント">
        <p className="text-sm text-muted-foreground leading-relaxed">
          エンドポイント詳細画面と共有ページに内蔵されているブラウザベースの HTTP クライアントです。
        </p>
        <SubSection title="使い方">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>URL とメソッドを確認（自動入力済み）</li>
            <li>必要に応じてリクエストヘッダーやボディを追加</li>
            <li>「送信」ボタンをクリック</li>
            <li>レスポンスのステータス・ヘッダー・ボディが表示されます</li>
          </ol>
        </SubSection>
        <SubSection title="機能">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>リクエスト履歴（最近のリクエストを再送）</li>
            <li>JSON レスポンスの自動整形</li>
            <li>curl コマンドのコピー</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="plans" title="プランと制限">
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">機能</th>
                <th className="px-4 py-2.5 text-center font-semibold">Free</th>
                <th className="px-4 py-2.5 text-center font-semibold">Pro ($9/月)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[
                ['プロジェクト数', `${limits.projects}つ`, '無制限'],
                ['エンドポイント数', `${limits.endpoints}件/プロジェクト`, '無制限'],
                ['リクエスト数', `${limits.freeRpm}回/分`, `${limits.proRpm}回/分`],
                ['ログ保持期間', `${limits.days}日`, `${limits.days}日`],
                ['共有URL', '✅', '✅'],
                ['シナリオエンジン', '✅', '✅'],
                ['Stateful Mock', '✅', '✅'],
              ].map(([feature, free, pro]) => (
                <tr key={feature} className="bg-background">
                  <td className="px-4 py-2.5 font-medium">{feature}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{free}</td>
                  <td className="px-4 py-2.5 text-center text-primary font-medium">{pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Note>プランの変更は <Link to="/billing" className="text-primary underline underline-offset-4">プラン・お支払い</Link> ページから行えます。</Note>
      </Section>

    </div>
  )
}

// ── Content: English ─────────────────────────────────────────────────────

function ContentEn() {
  const limits = planLimitVars(usePlanLimits())
  return (
    <div className="space-y-12">

      <Section id="getting-started" title="Getting Started">
        <p className="text-sm text-muted-foreground leading-relaxed">
          ScenarioMock lets you create and share Mock APIs that behave like the real thing.
          Key features include stateful mocks (resources created with POST can be retrieved with GET)
          and a scenario engine (e.g., return 500 on the 3rd request).
        </p>
        <SubSection title="1. Create an Account">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Go to the <Link to="/signup" className="text-primary underline underline-offset-4">Sign Up page</Link> and enter your email and password</li>
            <li>Agree to the Terms of Service and Privacy Policy</li>
            <li>You'll be redirected to the dashboard</li>
          </ol>
        </SubSection>
        <SubSection title="2. Basic Workflow">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Register a domain in Account Settings (this becomes your Mock URL prefix)</li>
            <li>Create a project (this determines your Mock URL slug)</li>
            <li>Add endpoints (e.g., <Code>GET /users</Code>)</li>
            <li>Configure scenarios (define responses)</li>
            <li>Send requests to the Mock URL to test</li>
            <li>Share the URL with your team</li>
          </ol>
        </SubSection>
      </Section>

      <Separator />

      <Section id="domain" title="Domain">
        <p className="text-sm text-muted-foreground leading-relaxed">
          A domain is the identifier that becomes your Mock URL prefix. <strong>After creating an account, you must register a domain first.</strong>
          Once registered, your personal URLs become active and you can create projects with slugs already taken by other users.
        </p>
        <SubSection title="What is a Domain?">
          <p className="text-sm text-muted-foreground">
            Your domain is used as a prefix in all your Mock URLs. The URL format after registration:
          </p>
          <CodeBlock>{`// With domain registered
https://yourdomain--slug.scenariomock.com

// Example: domain "yoshi", slug "my-api"
https://yoshi--my-api.scenariomock.com`}</CodeBlock>
          <Note>
            Domains are unique across all users — no two accounts can share the same domain.
            After registering, slugs only need to be unique within your own account.
          </Note>
        </SubSection>
        <SubSection title="How to Register">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Click "Account Settings" in the sidebar</li>
            <li>Enter your desired domain name (3–20 chars, lowercase letters, numbers, and hyphens only)</li>
            <li>Click "Save"</li>
          </ol>
        </SubSection>
        <SubSection title="Changing Your Domain">
          <p className="text-sm text-muted-foreground">
            You can change your domain at any time. However, <strong>changing it will update all your Mock URLs</strong>.
            Any clients using the old URLs will stop working, so be sure to update them.
          </p>
        </SubSection>
      </Section>

      <Separator />

      <Section id="project" title="Projects">
        <p className="text-sm text-muted-foreground leading-relaxed">
          A project is one Mock API server. Each project maps to a unique subdomain.
        </p>
        <SubSection title="Creating a Project">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Click "New Project" on the dashboard</li>
            <li><strong>Slug</strong>: Identifier for your Mock URL (lowercase letters, numbers, hyphens, 3–32 chars)</li>
            <li><strong>Name</strong>: Display name for management</li>
            <li><strong>Visibility</strong>: Set to Public to enable sharing</li>
          </ol>
          <Note>
            Example: slug <Code>my-api</Code>, domain <Code>yoshi</Code> → Mock URL: <Code>https://yoshi--my-api.scenariomock.com</Code>
          </Note>
        </SubSection>
        <SubSection title="Public vs Private">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>Private</strong>: Only you can manage it from the dashboard</li>
            <li><strong>Public</strong>: Anyone can view the endpoint list and send requests via the shared URL</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="endpoint" title="Endpoints">
        <p className="text-sm text-muted-foreground leading-relaxed">
          An endpoint is a route in your Mock API, defined by an HTTP method and a path.
        </p>
        <SubSection title="Adding an Endpoint">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Click "Add Endpoint" on the project page</li>
            <li><strong>Method</strong>: GET / POST / PUT / PATCH / DELETE / HEAD</li>
            <li><strong>Path</strong>: e.g., <Code>/users</Code> or <Code>/users/:id</Code> (must start with <Code>/</Code>)</li>
            <li><strong>Priority</strong>: When multiple endpoints match, higher priority wins</li>
          </ol>
        </SubSection>
        <SubSection title="Path Parameters">
          <p className="text-sm text-muted-foreground">
            Dynamic segments like <Code>:id</Code> are supported.
            <Code>/users/:id</Code> matches <Code>/users/123</Code>.
          </p>
        </SubSection>
        <SubSection title="Free Plan Limit">
          <Note>Free plan is limited to <strong>{limits.endpoints} endpoints</strong> per project. Pro plan is unlimited.</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-static" title="Scenario: Static">
        <p className="text-sm text-muted-foreground leading-relaxed">
          The simplest scenario type. Always returns the same response.
        </p>
        <SubSection title="Configuration">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>Status code</strong>: 200, 201, 400, 404, 500, etc.</li>
            <li><strong>Response body</strong>: JSON, text, or XML</li>
            <li><strong>Response headers</strong>: Add custom headers</li>
            <li><strong>Delay (ms)</strong>: Simulate network latency</li>
          </ul>
        </SubSection>
        <SubSection title="Example">
          <CodeBlock>{`// GET /users → 200 OK
{
  "users": [
    { "id": 1, "name": "Alice" },
    { "id": 2, "name": "Bob" }
  ]
}`}</CodeBlock>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-sequence" title="Scenario: Sequence">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Returns different responses in sequence on each request. Create behaviors like "200 for the first 2 requests, 500 on the 3rd."
        </p>
        <SubSection title="Configuration">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>Steps</strong>: Define responses in order (add as many as you need)</li>
            <li><strong>Loop</strong>: When ON, cycles back to step 1 after the last step</li>
            <li><strong>Scope</strong>: Share counter globally or per IP address</li>
          </ul>
        </SubSection>
        <SubSection title="Example">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Step 1: 200 OK (normal response)</li>
            <li>Step 2: 200 OK (normal response)</li>
            <li>Step 3: 500 Internal Server Error</li>
          </ul>
          <Note>You can reset the counter from the scenario detail page.</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-conditional" title="Scenario: Conditional">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Returns different responses based on request conditions (headers, query params, body, or method).
        </p>
        <SubSection title="Condition Types">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>header</strong>: Match on request headers (e.g., no Authorization → 401)</li>
            <li><strong>query</strong>: Match on query parameters</li>
            <li><strong>body</strong>: Match on JSON body fields</li>
            <li><strong>method</strong>: Match on HTTP method</li>
          </ul>
        </SubSection>
        <SubSection title="Operators">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><Code>equals</Code> / <Code>not_equals</Code></li>
            <li><Code>contains</Code> / <Code>not_contains</Code></li>
            <li><Code>exists</Code> / <Code>not_exists</Code> (field presence check)</li>
            <li><Code>regex</Code> (regular expression match)</li>
          </ul>
        </SubSection>
        <SubSection title="Example">
          <p className="text-sm text-muted-foreground">Return 401 when Authorization header is missing:</p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Condition: <Code>header.authorization</Code> <Code>not_exists</Code></li>
            <li>Response: 401 Unauthorized</li>
            <li>Default (when no condition matches): 200 OK</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="scenario-stateful" title="Scenario: Stateful Collection">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Recreates a real CRUD API where you can create resources with POST and retrieve them with GET.
          Data is stored in Redis.
        </p>
        <SubSection title="Actions">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li><strong>create</strong>: POST to create a resource (auto-generates ID)</li>
            <li><strong>list</strong>: GET all resources</li>
            <li><strong>get</strong>: GET a single resource by ID</li>
            <li><strong>update</strong>: PUT / PATCH to update a resource</li>
            <li><strong>delete</strong>: DELETE a resource</li>
          </ul>
        </SubSection>
        <SubSection title="Example">
          <CodeBlock>{`# Create a resource
POST https://yourdomain--my-api.scenariomock.com/users
{"name": "Alice", "email": "alice@example.com"}
→ {"id": "uuid-xxx", "name": "Alice", "email": "alice@example.com"}

# List all
GET https://yourdomain--my-api.scenariomock.com/users
→ [{"id": "uuid-xxx", "name": "Alice", ...}]

# Get by ID
GET https://yourdomain--my-api.scenariomock.com/users/uuid-xxx
→ {"id": "uuid-xxx", "name": "Alice", ...}

# Delete
DELETE https://yourdomain--my-api.scenariomock.com/users/uuid-xxx
→ 204 No Content`}</CodeBlock>
          <Note>You can view and reset collection data in real time from the "Collection" panel on the scenario detail page.</Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="sharing" title="Sharing URL">
        <p className="text-sm text-muted-foreground leading-relaxed">
          When a project is set to Public, you can share its endpoint list via a sharing URL.
          Recipients can send requests directly from their browser without an account.
        </p>
        <SubSection title="Sharing URL Format">
          <CodeBlock>{`https://scenariomock.com/shared/your-slug
// or
https://your-slug.scenariomock.com`}</CodeBlock>
        </SubSection>
        <SubSection title="What Recipients Can Do">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>View the endpoint list</li>
            <li>Send requests from the built-in API client</li>
            <li>View request logs in real time</li>
          </ul>
          <Note>If the owner is logged in, an "Admin" button appears to go directly to the management page.</Note>
        </SubSection>
        <SubSection title="When Listed in the Shared Gallery">
          <p className="text-sm text-muted-foreground leading-relaxed">
            A Public project can be listed in the shared gallery from the dashboard (a display name is required).
            Anyone can write data to a listed mock, so while it is listed, the following are reset automatically every 6 hours:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Data stored by Stateful Collection (IDs start again from 1)</li>
            <li>Sequence counters</li>
          </ul>
          <Note>
            Nothing is cleared right after you list a project. The reset happens on the first request after 6 hours have passed since the first request.
            Unlisting stops the reset; listing again starts a new 6-hour period.
          </Note>
        </SubSection>
      </Section>

      <Separator />

      <Section id="api-client" title="API Client">
        <p className="text-sm text-muted-foreground leading-relaxed">
          A browser-based HTTP client built into the endpoint detail page and shared page.
        </p>
        <SubSection title="How to Use">
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Check the URL and method (pre-filled automatically)</li>
            <li>Add request headers or body as needed</li>
            <li>Click "Send"</li>
            <li>The response status, headers, and body are displayed</li>
          </ol>
        </SubSection>
        <SubSection title="Features">
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Request history (resend recent requests)</li>
            <li>Automatic JSON formatting</li>
            <li>Copy as curl command</li>
          </ul>
        </SubSection>
      </Section>

      <Separator />

      <Section id="plans" title="Plans & Limits">
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Feature</th>
                <th className="px-4 py-2.5 text-center font-semibold">Free</th>
                <th className="px-4 py-2.5 text-center font-semibold">Pro ($9/mo)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[
                ['Projects', limits.projects, 'Unlimited'],
                ['Endpoints', `${limits.endpoints} / project`, 'Unlimited'],
                ['Requests', `${limits.freeRpm} / min`, `${limits.proRpm} / min`],
                ['Log retention', `${limits.days} days`, `${limits.days} days`],
                ['Sharing URL', '✅', '✅'],
                ['Scenario Engine', '✅', '✅'],
                ['Stateful Mock', '✅', '✅'],
              ].map(([feature, free, pro]) => (
                <tr key={feature} className="bg-background">
                  <td className="px-4 py-2.5 font-medium">{feature}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{free}</td>
                  <td className="px-4 py-2.5 text-center text-primary font-medium">{pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Note>You can change your plan from the <Link to="/billing" className="text-primary underline underline-offset-4">Plan & Billing</Link> page.</Note>
      </Section>

    </div>
  )
}

// ── Content (reusable inside AppLayout or standalone page) ───────────────

export function DocsContent() {
  const { i18n } = useTranslation()
  const isJa = i18n.language === 'ja'
  const toc = isJa ? TOC_JA : TOC_EN
  const [activeId, setActiveId] = useState('')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        }
      },
      { rootMargin: '-20% 0% -70% 0%' }
    )
    toc.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [isJa, toc])

  return (
    <div className="mx-auto max-w-5xl w-full flex gap-8 px-6 py-10 flex-1">
      {/* TOC sidebar */}
      <aside className="hidden lg:block w-48 shrink-0">
        <div className="sticky top-6 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {isJa ? '目次' : 'Contents'}
          </p>
          {toc.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`block text-sm py-1 px-2 rounded transition-colors ${
                activeId === id
                  ? 'text-primary font-medium bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </a>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">
            {isJa ? 'マニュアル' : 'Documentation'}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {isJa ? 'ScenarioMock の使い方ガイド' : 'How to use ScenarioMock'}
          </p>
        </div>
        {isJa ? <ContentJa /> : <ContentEn />}
      </main>
    </div>
  )
}

// ── Standalone public page ────────────────────────────────────────────────

export function DocsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <DocsContent />
    </div>
  )
}
